import { computed, shallowRef, watch } from 'vue';
import type {
  Role,
  UserRoleCatalogDTO,
  UserRoleCatalogMeta,
  UserRoleCatalogRouteGroup
} from '@krafta/shared-types';

import type { AuthHeaders } from '@shared/api/httpClient';
import { useAuthContext } from '@shared/context/useAuthContext';
import { fetchUserRoleCatalogAPI } from '@shared/reference-catalogs/userRoleCatalogApi';

export type UserRoleOption = {
  value: Role;
  label: string;
  meta?: UserRoleCatalogMeta;
  sortOrder?: number;
  tone?: string;
};

const catalog = shallowRef<UserRoleCatalogDTO | null>(null);
const loading = shallowRef(false);
const error = shallowRef<string | null>(null);
let loadingPromise: Promise<void> | null = null;

type AuthHeadersSource = AuthHeaders | (() => AuthHeaders);
type UseUserRoleCatalogOptions = {
  authHeaders?: AuthHeadersSource;
};
type RoleListSource = readonly Role[] | (() => readonly Role[] | null | undefined);
type UseUserRoleRouteGroupsOptions = UseUserRoleCatalogOptions & {
  roles: RoleListSource;
};

const readAuthHeaders = (source: AuthHeadersSource) =>
  typeof source === 'function' ? source() : source;
const readRoleList = (source: RoleListSource): readonly Role[] =>
  typeof source === 'function' ? (source() ?? []) : source;

const activeRoleItems = (currentCatalog: UserRoleCatalogDTO | null) =>
  (currentCatalog?.items ?? []).filter((item) => item.active ?? true);

const toRoleOptions = (currentCatalog: UserRoleCatalogDTO | null): UserRoleOption[] =>
  activeRoleItems(currentCatalog).map((item) => ({
    value: item.value,
    label: item.label,
    meta: item.meta,
    sortOrder: item.sortOrder,
    tone: item.tone
  }));

const toExecutorRoleOptions = (options: UserRoleOption[]) =>
  options
    .filter((option) => option.meta?.executor === true)
    .sort((left, right) => (left.meta?.executorSortOrder ?? 0) - (right.meta?.executorSortOrder ?? 0));

const readDefaultRole = (currentCatalog: UserRoleCatalogDTO | null): Role | '' => {
  const items = activeRoleItems(currentCatalog);
  const defaultItem = items.find((item) => item.meta?.default === true);
  return defaultItem?.value ?? items[0]?.value ?? '';
};

export const clearUserRoleCatalogCache = () => {
  catalog.value = null;
  loading.value = false;
  error.value = null;
  loadingPromise = null;
};

export const getUserRoleCatalogRolesByRouteGroup = (group: UserRoleCatalogRouteGroup): Role[] =>
  activeRoleItems(catalog.value)
    .filter((item) => (item.meta?.accessGroups ?? item.meta?.routeGroups ?? []).includes(group))
    .map((item) => item.value);

export const userHasRoleCatalogRouteGroup = (
  roles: readonly Role[] | null | undefined,
  group: UserRoleCatalogRouteGroup
) => {
  const routeGroupRoles = getUserRoleCatalogRolesByRouteGroup(group);
  return routeGroupRoles.length > 0 && routeGroupRoles.some((role) => roles?.includes(role));
};

export const loadUserRoleCatalog = async (headers: AuthHeaders): Promise<void> => {
  if (catalog.value) return;
  if (loadingPromise) return loadingPromise;

  loading.value = true;
  error.value = null;

  loadingPromise = fetchUserRoleCatalogAPI(headers)
    .then((loadedCatalog) => {
      catalog.value = loadedCatalog;
    })
    .catch((cause: unknown) => {
      error.value = cause instanceof Error ? cause.message : 'Не удалось загрузить роли';
      catalog.value = null;
    })
    .finally(() => {
      loading.value = false;
      loadingPromise = null;
    });

  return loadingPromise;
};

export const useUserRoleCatalog = (options: UseUserRoleCatalogOptions = {}) => {
  const contextAuth = options.authHeaders ? null : useAuthContext();
  const getAuthHeaders = () =>
    options.authHeaders ? readAuthHeaders(options.authHeaders) : (contextAuth?.authHeaders ?? {});

  void loadUserRoleCatalog(getAuthHeaders());

  const roleOptions = computed(() => toRoleOptions(catalog.value));
  const executorRoleOptions = computed(() => toExecutorRoleOptions(roleOptions.value));
  const roles = computed(() => roleOptions.value.map((option) => option.value));
  const defaultRole = computed(() => readDefaultRole(catalog.value));
  const roleOption = (role?: string | null) => roleOptions.value.find((option) => option.value === role) ?? null;
  const roleLabel = (role?: string | null) =>
    roleOption(role)?.label ?? role ?? 'Без роли';
  const roleTone = (role?: string | null) => roleOption(role)?.tone ?? null;

  return {
    roleCatalog: catalog,
    executorRoleOptions,
    roleOptions,
    roles,
    defaultRole,
    roleCatalogLoading: loading,
    roleCatalogError: error,
    loadRoleCatalog: () => loadUserRoleCatalog(getAuthHeaders()),
    roleLabel,
    roleOption,
    roleTone
  };
};

export const useUserRoleRouteGroups = (options: UseUserRoleRouteGroupsOptions) => {
  const contextAuth = options.authHeaders ? null : useAuthContext();
  const getAuthHeaders = () =>
    options.authHeaders ? readAuthHeaders(options.authHeaders) : (contextAuth?.authHeaders ?? {});

  watch(
    () => readRoleList(options.roles),
    (roles) => {
      if (roles.length > 0) {
        void loadUserRoleCatalog(getAuthHeaders());
      }
    },
    { immediate: true }
  );

  const hasRouteGroup = (group: UserRoleCatalogRouteGroup) =>
    userHasRoleCatalogRouteGroup(readRoleList(options.roles), group);

  return {
    hasRouteGroup
  };
};
